import { type CSSProperties, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { Invoice } from '../components'
import { usePurchases } from './usePurchases'
import { calculatePurchaseItemsValue, fullDate, substract, devide } from '../utils'

export function Purchases() {
	const { getPurchases } = usePurchases()
	const { data } = getPurchases()
	const { t } = useTranslation()
	const [printMode, setPrintMode] = useState<'normal' | 'fit'>('normal')

	useEffect(() => {
		document.body.dataset.receiptPrintMode = printMode

		return () => {
			delete document.body.dataset.receiptPrintMode
		}
	}, [printMode])

	if (!data?.invoicePurchase) return <></>

	const purchase: Purchase = data.invoicePurchase
	const purchaseTotal = calculatePurchaseItemsValue(purchase.purchaseItems)
	const rest = substract(purchaseTotal, purchase.paid)
	const title = t('purchase.title', { name: purchase.client.name })
	const notesRows = purchase.notes ? 1 : 0
	const itemRows = purchase.purchaseItems.reduce(
		(total, item) => total + 4 + (item.extraCosts ? 1 : 0),
		0
	)
	const printRowCount = notesRows + itemRows + 6
	const printScale = Math.max(0.42, Math.min(1, 12 / printRowCount))
	const printReceipt = (mode: 'normal' | 'fit') => {
		setPrintMode(mode)
		document.body.dataset.receiptPrintMode = mode
		setTimeout(() => window.print(), 0)
	}

	return (
		<div>
			<Helmet>
				<title>{title}</title>
			</Helmet>
			<Invoice title={title}>
				<div className='table-container'>
					<div className="print-mode-toggle">
						<button
							className={printMode === 'normal' ? 'active' : ''}
							onClick={() => printReceipt('normal')}
							type="button"
						>
							طباعة عادية
						</button>
						<button
							className={printMode === 'fit' ? 'active' : ''}
							onClick={() => printReceipt('fit')}
							type="button"
						>
							طباعة في صفحة واحدة
						</button>
					</div>
					<div
						className={`receipt-stack ${printMode === 'fit' ? 'receipt-fit-one-page' : 'receipt-normal-print'}`}
						style={{ '--receipt-print-scale': printScale } as CSSProperties & Record<string, number>}
					>
						{purchase.notes ? (
							<div className="receipt-block">
								<table className="dre receipt-table">
									<tbody>
										<tr>
											<td>ملاحظات</td>
											<td className="text-break-spaces">{purchase.notes}</td>
										</tr>
									</tbody>
								</table>
							</div>
						) : null}
						{purchase.purchaseItems.map((purchaseItem, index) => (
							<div className="receipt-block" key={`products_list_${index}`}>
								<table className="dre receipt-table">
									<tbody>
										<tr className="receipt-item">
											<td><strong>{t('purchase.item')} {index + 1}</strong></td>
											<td><strong>{purchaseItem.product.name}</strong></td>
										</tr>
										<tr>
											<td>{t('purchase.amount')}</td>
											<td>
												{t('amountWithUnit', {
													amount: purchaseItem.amount,
													unit: t(`unitsTranslation.${purchaseItem.product.unit}`),
												})}
											</td>
										</tr>
										<tr>
											<td>
												{t('purchase.unitPrice', {
													unit: t(`unitsTranslation.${purchaseItem.product.unit}`),
												})}
											</td>
											<td>
												{t('purchase.unitPriceValue', {
													value: devide(purchaseItem.price, purchaseItem.amount)
												})}
											</td>
										</tr>
										<tr>
											<td>
												{t('purchase.priceTotal')}
											</td>
											<td>
												{t('priceWithSign', { price: purchaseItem.price })}
											</td>
										</tr>
										{purchaseItem.extraCosts ? (
											<tr>
												<td>مصاريف إضافية</td>
												<td>{t('priceWithSign', { price: purchaseItem.extraCosts })}</td>
											</tr>
										) : null}
									</tbody>
								</table>
							</div>
						))}
						<div className="receipt-block receipt-summary">
							<table className="dre receipt-table">
								<tbody>
									<tr className="receipt-paid">
										<td> {t('purchase.paid')}</td>
										<td>
											{t('priceWithSign', { price: purchase.paid })}
										</td>
									</tr>
									<tr className="receipt-balance">
										<td> {t('purchase.rest')}</td>
										<td>
											{t('priceWithSign', { price: rest })}
										</td>
									</tr>
									<tr>
										<td>
											{t('purchase.driverName')}
										</td>
										<td> {purchase.driverName || '-'}</td>
									</tr>
									<tr>
										<td>
											{t('purchase.driverPhone')}
										</td>
										<td dir="ltr"> {purchase.driverPhone || '-'}</td>
									</tr>
									<tr>
										<td>
											{t('product.createdAt')}
										</td>
										<td> {fullDate(purchase.createdAt)}</td>
									</tr>
									<tr>
										<td>
											{t('product.updatedAt')}
										</td>
										<td> {fullDate(purchase.updatedAt)}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</Invoice>
		</div>
	)
}
